<template>
  <UIDropdown
    :items="options"
    class="DropdownWebsiteVisibility"
    :isDisabled="isDisabled"
    @clickedOption="selectOption"
  >
    <template #toggle>
      <WebsiteVisibilityOption
        v-if="selectedOption"
        :key="selectedOption.value"
        :label="selectedOption.label"
        :description="selectedOption.description"
        :icon="selectedOption.icon"
        class="DropdownWebsiteVisibility__active-item"
      />
    </template>

    <!-- ui-lib's list item owns the click, close and keyboard handling, so the row
         only carries content. -->
    <template #option="{ option }">
      <WebsiteVisibilityOption
        class="DropdownWebsiteVisibility__list-item"
        :label="optionFor(option.value).label"
        :description="optionFor(option.value).description"
        :icon="optionFor(option.value).icon"
        :isSelected="modelValue === option.value"
      />
    </template>
  </UIDropdown>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import { UIDropdown } from '@foundever/ui-lib'

import WebsiteVisibilityOption from './WebsiteVisibilityOption.vue'

import type { IWebsiteVisibilityOption, WebsiteVisibilityMode } from '../types'

const props = withDefaults(
  defineProps<{
    modelValue: WebsiteVisibilityMode
    isDisabled?: boolean
    /** Offer the password-protected option (gated by the caller's feature flag). */
    allowPassword?: boolean
  }>(),
  { allowPassword: true },
)

const emit = defineEmits<{
  'update:modelValue': [value: WebsiteVisibilityMode]
}>()

const allOptions: IWebsiteVisibilityOption[] = [
  {
    value: 'foundever',
    label: 'All Foundever users',
    description: 'All with a Foundever login can access this webpage',
    icon: 'logo',
  },
  {
    value: 'private',
    label: 'Private',
    description: 'Only you and selected Foundever users can view this webpage',
    icon: 'usersRegular',
  },
  {
    value: 'password',
    label: 'Password protected',
    description:
      'Only Foundever users with the correct password can access this webpage',
    icon: 'lockRegular',
  },
]

/**
 * Password protection is a flagged option: with it off the mode is not offered,
 * so an existing page can't be switched onto it.
 */
const options = computed(() =>
  props.allowPassword
    ? allOptions
    : allOptions.filter((option) => option.value !== 'password'),
)

const selectedOption = ref<IWebsiteVisibilityOption>(allOptions[0])

const optionByMode = computed(
  () =>
    Object.fromEntries(
      options.value.map((option) => [option.value, option]),
    ) as Record<WebsiteVisibilityMode, IWebsiteVisibilityOption>,
)

/** ui-lib types menu rows as bare `{ label, value, icon? }`, so the row reads its
 *  full option (description, required icon) back from the mode it carries. */
function optionFor(mode: string) {
  return optionByMode.value[mode as WebsiteVisibilityMode] ?? options.value[0]
}

function selectOption(option: { value: string }) {
  const mode = option.value as WebsiteVisibilityMode
  if (props.modelValue === mode) return
  selectedOption.value = optionByMode.value[mode] ?? selectedOption.value
  emit('update:modelValue', mode)
}

watch(
  () => props.modelValue,
  (mode) => {
    selectedOption.value = optionByMode.value[mode] ?? options.value[0]
  },
  { immediate: true },
)
</script>

<style scoped lang="scss">
.DropdownWebsiteVisibility {
  width: 100%;

  :deep(.UIDropdown__trigger) {
    width: 100%;
  }
  // ui-lib's toggle is a single-line select chrome; this one holds a two-line
  // option (label + description), so it needs the taller box and inner padding.
  :deep(.UIDropdownToggle) {
    // ui-lib's toggle resolves its resting border from `--interactive-border-default`
    // (#d9d9de); shadowing that token here lands the design's softer --border-default
    // without outranking ui-lib's own hover/open border rules.
    --interactive-border-default: var(--border-default);

    height: auto;
    gap: var(---fds-global-space-sm);
    padding: var(---fds-global-space-md);
    border-radius: var(--radius-md);
    background-color: var(--bg-tertiary);

    &:hover {
      background-color: var(--bg-secondary);
    }

    .UIDropdownToggle__icon {
      --color: var(--icon-tertiary);
    }
  }
}
</style>

<!--
  ui-lib's UIDropdown teleports its menu to <body> with no per-instance content
  class, so the rows are styled globally, keyed on this dropdown's own row class.
-->
<style lang="scss">
// ui-lib sizes the floating panel to `min-width: <trigger width>`; `width: min-content`
// keeps it from also growing to the widest option description, so the open menu lines
// up with the closed field instead of spilling past the modal. `border-box` is what
// makes that min-width land on the panel's outer edge — ui-lib only resets `html`.
.UIDropdown__content:has(.DropdownWebsiteVisibility__list-item) {
  box-sizing: border-box;
  width: min-content;
}

// The menu box already pads by `xs`; the item adds another `xs` so an option's
// content sits `md` from the panel edge — the same inset as the closed trigger,
// keeping the icons aligned as the dropdown opens.
.UIDropdownListItem:has(.DropdownWebsiteVisibility__list-item) {
  min-width: 0;
  padding: var(---fds-global-space-xs);
  border-radius: var(--radius-s);

  &:hover {
    background-color: var(--bg-tertiary);
  }
}
</style>
